import type { CustomerEnterpriseQueryParams, CustomerEnterpriseVo, Page } from "../../../entity";

import httpRequest from "@/api/index";
import { confirmable } from "@/utils/decorator/confirmable";

const PATH = "/customer/sub";

const service = httpRequest;

/**
 * 分页查询企业邀请/加入的受邀记录列表
 * @param queryParams 查询参数（分页参数/状态）
 * @returns 分页的客户企业VO列表数据
 */
export const queryEnterpriseInvitedRecordPageList = async (queryParams: {
    page: number;
    size: number;
    state: number;
}): Promise<Page<CustomerEnterpriseVo>> => {
    const { data } = await service.get(`${PATH}/invited/paging`, { params: queryParams });
    return data;
};

/**
 * 分页查询企业邀请记录（传入「已确认」状态则查询子账号列表）
 * @param queryParams 客户企业查询参数
 * @returns 分页的客户企业VO列表数据
 */
export const queryEnterpriseInvitationRecordOrSubAccountPageList = async (
    queryParams: CustomerEnterpriseQueryParams
): Promise<Page<CustomerEnterpriseVo>> => {
    const { data } = await service.get(`${PATH}/paging`, { params: queryParams });
    return data;
};

/**
 * 邀请子账号加入企业
 * @param formData 邀请参数（手机号/权限列表）
 * @returns 分页的客户企业VO列表数据
 */
export const inviteSubAccountToEnterprise = async (formData: {
    mobile: string;
    permissions: number[];
}): Promise<Page<CustomerEnterpriseVo>> => {
    const { data } = await service.post(`${PATH}`, formData);
    return data;
};

/**
 * 删除企业子账号（带删除确认弹窗：提示删除后信息不展示）
 * @param subAccountId 子账号ID
 * @param operationType 操作类型标识
 * @returns 分页的客户企业VO列表数据
 */
export const deleteEnterpriseSubAccount = confirmable("是否删除？删除后此信息不再展示")(async (
    subAccountId: number,
    operationType: number
): Promise<Page<CustomerEnterpriseVo>> => {
    const { data } = await service.delete(`${PATH}/remove/${subAccountId}?operationType=${operationType}`, {});
    return data;
});

/**
 * 确认企业邀请（带确认弹窗：提示确认后可切换企业）
 * @param invitationId 邀请记录ID
 * @param state 确认状态值
 * @returns 确认操作接口响应数据
 */
export const confirmEnterpriseInvitation = confirmable("是否确认？ 确认后可以切换至对应企业")(async (
    invitationId: number,
    state: number
) => {
    const { data } = await service.put(`${PATH}/confirm/${invitationId}?state=${state}`, {});
    return data;
});

/**
 * 拒绝企业邀请（带确认弹窗：提示拒绝后受邀记录不展示）
 * @param invitationId 邀请记录ID
 * @param state 拒绝状态值
 * @returns 拒绝操作接口响应数据
 */
export const rejectEnterpriseInvitation = confirmable("是否拒绝？ 拒绝后将不在受邀记录中展示")(async (
    invitationId: number,
    state: number
) => {
    const { data } = await service.put(`${PATH}/confirm/${invitationId}?state=${state}`, {});
    return data;
});

/**
 * 获取企业子账号详情（PC端专用：写死查询子账号类型）
 * @param subAccountId 子账号ID
 * @returns 客户企业VO详情数据
 */
export const getEnterpriseSubAccountDetail = async (subAccountId: number): Promise<CustomerEnterpriseVo> => {
    // 小程序由于受邀记录和子账号信息邀请记录详情用的同一个接口，要区分账号信息是子账号还是主账号的，用户PC只有子账号信息有详情所以写死查子账号信息
    enum QueryType {
        // 查询子账号
        SUB_ACCOUNT = 2
    }
    const { data } = await service.get(`${PATH}/find/vo/${subAccountId}?queryType=${QueryType.SUB_ACCOUNT}`);
    return data;
};

/**
 * 子账号登录时获取自身的企业子账号详情
 * @returns 客户企业VO详情数据
 */
export const getCurrentSubAccountEnterpriseDetail = async (): Promise<CustomerEnterpriseVo> => {
    const { data } = await service.get(`${PATH}/find/self/vo`);
    return data;
};

/**
 * 编辑企业子账号信息（带保存确认弹窗）
 * @param subAccountId 子账号ID
 * @param formData 编辑参数（权限列表/短信接收状态/账号状态）
 * @returns 分页的客户企业VO列表数据
 */
export const editEnterpriseSubAccountInfo = confirmable("是否保存")(async (
    subAccountId: number,
    formData: { permissions: number[]; receiptSms: number; accountState: number }
): Promise<Page<CustomerEnterpriseVo>> => {
    const { data } = await service.put(`${PATH}/${subAccountId}`, formData);
    return data;
});

/**
 * 获取短信管理的企业用户及子账号列表数据
 * @returns 客户企业VO数组
 */
export const getSmsManagementEnterpriseUserAndSubAccountList = async (): Promise<CustomerEnterpriseVo[]> => {
    const { data } = await service.get(`${PATH}/invited/list`);
    return data;
};

/**
 * 提交申请加入企业的请求
 * @param customerId 申请加入企业的客户ID
 * @param bodyId 企业主体ID
 */
export const submitApplyToJoinEnterprise = async (customerId: number, bodyId: number) => {
    await service.post(`${PATH}/add-enterprise?customerId=${customerId}&bodyId=${bodyId}`, {});
};

/**
 * 分页查询申请加入企业的记录列表
 * @param queryParams 查询参数（排序方向/姓名/手机号等）
 * @returns 分页的客户企业VO列表数据
 */
export const queryApplyToJoinEnterpriseRecordPageList = async (
    queryParams: CustomerEnterpriseQueryParams
): Promise<Page<CustomerEnterpriseVo>> => {
    const { data } = await service.get(`${PATH}/apply/paging`, { params: queryParams });
    return data;
};

/**
 * 确认或拒绝加入企业的申请
 * @param applyId 申请记录ID
 * @param formData 操作参数（状态/权限列表<可选>）
 */
export const confirmOrRejectApplyToJoinEnterprise = async (
    applyId: number,
    formData: { state: number; permissions?: number[] }
) => {
    await service.put(`${PATH}/confirm/enterprise/${applyId}`, formData);
};

/**
 * 删除加入企业的申请/记录（带删除确认弹窗）
 * @param applyId 加入企业的申请/记录ID
 */
export const deleteApplyToJoinEnterpriseRecord = confirmable("是否删除")(async (applyId: number) => {
    await service.delete(`${PATH}/remove-enterprise/${applyId}`, {});
});
